// @flow
/**
 * @module Reducers/Dashboard
 * @desc Dashboard Reducer
 */

import { handleActions } from 'redux-actions';
import { DashboardConstants } from 'constants/dashboard';
import { AuthConstants } from 'constants/auth';
import { requestPending, requestSuccess, requestFail } from 'components/Fetch';

const findLoanById = (loans, loanId) => {
  if (loanId == null || loanId === '' || !Array.isArray(loans)) {
    return null;
  }

  const key = String(loanId);
  return loans.find((loan) => String(loan?.loanId) === key) || null;
};

const persistSelectedLoanId = (loanId) => {
  if (loanId == null || loanId === '') {
    return;
  }

  localStorage.setItem('selectedLoanId', String(loanId));
};

const loadingLoanState = {
  isLoading: true,
};

const loansList = sessionStorage.getItem('loansList');
const savedLoanId = localStorage.getItem('selectedLoanId');
const initialState = {
  loansList: {
    isLoading: false,
    activeLoanId: (() => {
      if (loansList !== null && JSON.parse(loansList).length > 0) {
        const parsedLoans = JSON.parse(loansList);
        const savedLoan = findLoanById(parsedLoans, savedLoanId);
        return savedLoan ? savedLoan.loanId : parsedLoans[0].loanId;
      }
      return null;
    })(),
    data: loansList !== null ? JSON.parse(loansList) : null,
  },
  customerDetail: { isLoading: false },
  loanDetail: { isLoading: false },
  loanTerm: { isLoading: false },
  transactionsList: { isLoading: false },
  paymentsList: { isLoading: false },
  paymentMethods: { isLoading: false },
  documents: { isLoading: false },
  notices: { isLoading: false },
  donwload: { isLoading: false },
  remove: { isLoading: false },
  putPaymentMethod: { isLoading: false },
  makePaymentMessage: { isLoading: false },
  currentPayoffInfo: { isLoading: false },
  futurePayoffInfo: { isLoading: false },
  scheduledPaymentsList: { isLoading: false },
  billingSetting: { isLoading: false },
  autopay: { isLoading: false },
  loanCharges: { isLoading: false },
  collateral: { isLoading: false },
  collateralSummary: { isLoading: false },
  collateralRequired: { isLoading: false },
  collateralLiquidations: { isLoading: false },
};

export default handleActions({
  [requestPending(DashboardConstants.FETCH_LOANS_LIST)]: (state) => ({
    ...state,
    // Keep the current list and selected loan while a refresh is in flight.
    // Replacing the slice with `{ isLoading: true }` dropped activeLoanId, and
    // the dashboard then skipped every loan-scoped request.
    loansList: {
      ...state.loansList,
      isLoading: true,
    },
  }),
  [requestSuccess(DashboardConstants.FETCH_LOANS_LIST)]: (state, action) => {
    const loans = Array.isArray(action.payload) ? action.payload : [];
    const currentLoanId = state.loansList?.activeLoanId;
    const hasCurrentLoan = currentLoanId != null && currentLoanId !== '';
    // Opening Dashboard refetches this list. A loan already chosen in Documents
    // or Dashboard must stay selected; do not fall back to the first account.
    const storedLoan = findLoanById(loans, localStorage.getItem('selectedLoanId'));
    const activeLoanId = hasCurrentLoan
      ? currentLoanId
      : (storedLoan ? storedLoan.loanId : (loans[0] ? loans[0].loanId : null));
    if (activeLoanId != null && activeLoanId !== '') {
      persistSelectedLoanId(activeLoanId);
    }
    if (action.payload) {
      sessionStorage.setItem('loansList', JSON.stringify(action.payload));
    }
    return {
      ...state,
      loansList: {
        data: action.payload,
        activeLoanId,
        isLoading: false,
      },
    };
  },
  [requestFail(DashboardConstants.FETCH_LOANS_LIST)]: (state, action) => ({
    ...state,
    loansList: {
      ...state.loansList,
      error: action.payload,
      isLoading: false,
    },
  }),

  [DashboardConstants.SELECT_LOAN_ID]: (state, action) => {
    const nextLoanId = action.payload;
    const currentLoanId = state.loansList?.activeLoanId;
    const sameLoan = currentLoanId != null
      && currentLoanId !== ''
      && String(currentLoanId) === String(nextLoanId);

    persistSelectedLoanId(nextLoanId);

    return {
      ...state,
      loansList: {
        ...state.loansList,
        activeLoanId: nextLoanId,
      },
      // Drop the previous loan's dashboard data so Dashboard cannot keep
      // rendering that account after the customer switches loans in Documents.
      ...(sameLoan ? {} : {
        loanDetail: loadingLoanState,
        loanTerm: loadingLoanState,
        transactionsList: loadingLoanState,
        paymentsList: loadingLoanState,
        scheduledPaymentsList: loadingLoanState,
        autopay: loadingLoanState,
        loanCharges: loadingLoanState,
        collateral: loadingLoanState,
        collateralSummary: loadingLoanState,
        collateralRequired: loadingLoanState,
        collateralLiquidations: loadingLoanState,
        notices: loadingLoanState,
        billingSetting: loadingLoanState,
      }),
    };
  },

  [requestPending(DashboardConstants.FETCH_CUSTOMER_DETAIL)]: (state) => ({
    ...state,
    customerDetail: {
      isLoading: true,
    },
  }),
  [requestSuccess(DashboardConstants.FETCH_CUSTOMER_DETAIL)]: (state, action) => {
    if (action.payload) {
      sessionStorage.setItem('customerDetail', JSON.stringify(action.payload));
    }
    return {
      ...state,
      customerDetail: {
        data: action.payload,
        isLoading: false,
      },
    };
  },
  [requestFail(DashboardConstants.FETCH_CUSTOMER_DETAIL)]: (state, action) => ({
    ...state,
    customerDetail: {
      error: action.payload,
      isLoading: false,
    },
  }),

  [requestPending(DashboardConstants.FETCH_LOAN_DETAIL)]: (state) => ({
    ...state,
    loanDetail: {
      ...state.loanDetail,
      isLoading: true,
    },
  }),
  [requestSuccess(DashboardConstants.FETCH_LOAN_DETAIL)]: (state, action) => ({
    ...state,
    loanDetail: {
      data: action.payload,
      isLoading: false,
    },
  }),
  [requestFail(DashboardConstants.FETCH_LOAN_DETAIL)]: (state, action) => ({
    ...state,
    loanDetail: {
      ...state.loanDetail,
      error: action.payload,
      isLoading: false,
    },
  }),

  [requestPending(DashboardConstants.FETCH_LOAN_TERM)]: (state) => ({
    ...state,
    loanTerm: {
      isLoading: true,
    },
  }),
  [requestSuccess(DashboardConstants.FETCH_LOAN_TERM)]: (state, action) => ({
    ...state,
    loanTerm: {
      data: action.payload,
      isLoading: false,
    },
  }),
  [requestFail(DashboardConstants.FETCH_LOAN_TERM)]: (state, action) => ({
    ...state,
    loanTerm: {
      error: action.payload,
      isLoading: false,
    },
  }),

  [requestPending(DashboardConstants.FETCH_TRANSACTIONS_LIST)]: (state) => ({
    ...state,
    transactionsList: {
      isLoading: true,
    },
  }),
  [requestSuccess(DashboardConstants.FETCH_TRANSACTIONS_LIST)]: (state, action) => ({
    ...state,
    transactionsList: {
      data: action.payload,
      isLoading: false,
    },
  }),
  [requestFail(DashboardConstants.FETCH_TRANSACTIONS_LIST)]: (state, action) => ({
    ...state,
    transactionsList: {
      error: action.payload,
      isLoading: false,
    },
  }),

  [requestPending(DashboardConstants.FETCH_DOCUMENTS)]: (state) => ({
    ...state,
    documents: {
      isLoading: true,
    },
  }),
  [requestSuccess(DashboardConstants.FETCH_DOCUMENTS)]: (state, action) => ({
    ...state,
    documents: {
      data: action.payload,
      isLoading: false,
    },
  }),
  [requestFail(DashboardConstants.FETCH_DOCUMENTS)]: (state, action) => ({
    ...state,
    documents: {
      error: action.payload,
      isLoading: false,
    },
  }),

  [requestPending(DashboardConstants.FETCH_NOTICES)]: (state) => ({
    ...state,
    notices: {
      isLoading: true,
    },
  }),
  [requestSuccess(DashboardConstants.FETCH_NOTICES)]: (state, action) => ({
    ...state,
    notices: {
      data: action.payload,
      isLoading: false,
    },
  }),
  [requestFail(DashboardConstants.FETCH_NOTICES)]: (state, action) => ({
    ...state,
    notices: {
      error: action.payload,
      isLoading: false,
    },
  }),

  [requestPending(DashboardConstants.FETCH_PAYMENTS_LIST)]: (state) => ({
    ...state,
    paymentsList: {
      isLoading: true,
    },
  }),
  [requestSuccess(DashboardConstants.FETCH_PAYMENTS_LIST)]: (state, action) => ({
    ...state,
    paymentsList: {
      data: action.payload,
      isLoading: false,
    },
  }),
  [requestFail(DashboardConstants.FETCH_PAYMENTS_LIST)]: (state, action) => ({
    ...state,
    paymentsList: {
      error: action.payload,
      isLoading: false,
    },
  }),
  [requestPending(DashboardConstants.FETCH_PAYMENT_METHODS)]: (state) => ({
    ...state,
    paymentMethods: {
      isLoading: true,
    },
  }),
  [requestSuccess(DashboardConstants.FETCH_PAYMENT_METHODS)]: (state, action) => ({
    ...state,
    paymentMethods: {
      data: action.payload,
      isLoading: false,
    },
  }),
  [requestFail(DashboardConstants.FETCH_PAYMENT_METHODS)]: (state, action) => ({
    ...state,
    paymentMethods: {
      error: action.payload,
      isLoading: false,
    },
  }),
  [requestPending(DashboardConstants.DOWNLOAD_DOCUMENT)]: (state) => ({
    ...state,
    donwload: {
      isLoading: true,
    },
  }),
  [requestSuccess(DashboardConstants.DOWNLOAD_DOCUMENT)]: (state, action) => ({
    ...state,
    donwload: {
      data: action.payload,
      isLoading: false,
    },
  }),
  [requestFail(DashboardConstants.DOWNLOAD_DOCUMENT)]: (state, action) => ({
    ...state,
    donwload: {
      error: action.payload,
      isLoading: false,
    },
  }),
  [requestPending(DashboardConstants.REMOVE_PAYMENTACCOUNT)]: (state) => ({
    ...state,
    remove: {
      isLoading: true,
    },
  }),
  [requestSuccess(DashboardConstants.REMOVE_PAYMENTACCOUNT)]: (state, action) => ({
    ...state,
    remove: {
      data: action.payload,
      isLoading: false,
    },
  }),
  [requestFail(DashboardConstants.REMOVE_PAYMENTACCOUNT)]: (state, action) => ({
    ...state,
    remove: {
      error: action.payload,
      isLoading: false,
    },
  }),
  [requestPending(DashboardConstants.PUT_PAYMENT_ACCOUNT)]: (state) => ({
    ...state,
    putPaymentMethod: {
      isLoading: true,
    },
  }),
  [requestSuccess(DashboardConstants.PUT_PAYMENT_ACCOUNT)]: (state, action) => ({
    ...state,
    putPaymentMethod: {
      data: action.payload,
      isLoading: false,
    },
  }),
  [requestFail(DashboardConstants.PUT_PAYMENT_ACCOUNT)]: (state, action) => ({
    ...state,
    putPaymentMethod: {
      error: action.payload,
      isLoading: false,
    },
  }),
  [requestPending(DashboardConstants.MAKE_PAYMENT)]: (state) => ({
    ...state,
    makePaymentMessage: {
      isLoading: true,
    },
  }),
  [requestSuccess(DashboardConstants.MAKE_PAYMENT)]: (state, action) => ({
    ...state,
    makePaymentMessage: {
      data: action.payload,
      isLoading: false,
    },
  }),
  [requestFail(DashboardConstants.MAKE_PAYMENT)]: (state, action) => ({
    ...state,
    makePaymentMessage: {
      error: action.payload,
      isLoading: false,
    },
  }),
  [requestPending(DashboardConstants.FETCH_CURRENT_PAYOFF_INFO)]: (state) => ({
    ...state,
    currentPayoffInfo: {
      isLoading: true,
    },
  }),
  [requestSuccess(DashboardConstants.FETCH_CURRENT_PAYOFF_INFO)]: (state, action) => ({
    ...state,
    currentPayoffInfo: {
      data: action.payload,
      isLoading: false,
    },
  }),
  [requestFail(DashboardConstants.FETCH_CURRENT_PAYOFF_INFO)]: (state, action) => ({
    ...state,
    currentPayoffInfo: {
      error: action.payload,
      isLoading: false,
    },
  }),
  [requestPending(DashboardConstants.FETCH_FUTURE_PAYOFF_INFO)]: (state) => ({
    ...state,
    futurePayoffInfo: {
      isLoading: true,
    },
  }),
  [requestSuccess(DashboardConstants.FETCH_FUTURE_PAYOFF_INFO)]: (state, action) => ({
    ...state,
    futurePayoffInfo: {
      data: action.payload,
      isLoading: false,
    },
  }),
  [requestFail(DashboardConstants.FETCH_FUTURE_PAYOFF_INFO)]: (state, action) => ({
    ...state,
    futurePayoffInfo: {
      error: action.payload,
      isLoading: false,
    },
  }),
  [requestPending(DashboardConstants.FETCH_SCHEDULED_PAYMENT)]: (state) => ({
    ...state,
    scheduledPaymentsList: {
      isLoading: true,
    },
  }),
  [requestSuccess(DashboardConstants.FETCH_SCHEDULED_PAYMENT)]: (state, action) => ({
    ...state,
    scheduledPaymentsList: {
      data: action.payload,
      isLoading: false,
    },
  }),
  [requestFail(DashboardConstants.FETCH_SCHEDULED_PAYMENT)]: (state, action) => ({
    ...state,
    scheduledPaymentsList: {
      error: action.payload,
      isLoading: false,
    },
  }),

  [requestPending(DashboardConstants.GET_BILLING_SETTING)]: (state) => ({
    ...state,
    billingSetting: {
      isLoading: true,
    },
  }),
  [requestSuccess(DashboardConstants.GET_BILLING_SETTING)]: (state, action) => ({
    ...state,
    billingSetting: {
      data: action.payload,
      isLoading: false,
    },
  }),
  [requestFail(DashboardConstants.GET_BILLING_SETTING)]: (state, action) => ({
    ...state,
    billingSetting: {
      error: action.payload,
      isLoading: false,
    },
  }),

  [requestPending(DashboardConstants.PUT_BILLING_SETTING)]: (state) => ({
    ...state,
    billingSetting: {
      isLoading: true,
      ...state.billingSetting,
    },
  }),
  [requestSuccess(DashboardConstants.PUT_BILLING_SETTING)]: (state) => ({
    ...state,
    billingSetting: {
      data: {
        eBilling: !state.billingSetting.data.eBilling,
      },
      isLoading: false,
    },
  }),
  [requestFail(DashboardConstants.PUT_BILLING_SETTING)]: (state, action) => ({
    ...state,
    billingSetting: {
      error: action.payload,
      isLoading: false,
      ...state.billingSetting,
    },
  }),

  [requestPending(DashboardConstants.FETCH_AUTOPAY_INFO)]: (state) => ({
    ...state,
    autopay: {
      isLoading: true,
    },
  }),
  [requestSuccess(DashboardConstants.FETCH_AUTOPAY_INFO)]: (state, action) => ({
    ...state,
    autopay: {
      data: action.payload,
      isLoading: false,
    },
  }),
  [requestFail(DashboardConstants.FETCH_AUTOPAY_INFO)]: (state, action) => ({
    ...state,
    autopay: {
      error: action.payload,
      isLoading: false,
    },
  }),

  [requestPending(DashboardConstants.FETCH_LOAN_CHARGES)]: (state) => ({
    ...state,
    loanCharges: {
      isLoading: true,
    },
  }),
  [requestSuccess(DashboardConstants.FETCH_LOAN_CHARGES)]: (state, action) => ({
    ...state,
    loanCharges: {
      data: action.payload,
      isLoading: false,
    },
  }),
  [requestFail(DashboardConstants.FETCH_LOAN_CHARGES)]: (state, action) => ({
    ...state,
    loanCharges: {
      error: action.payload,
      isLoading: false,
    },
  }),

  [requestPending(DashboardConstants.FETCH_COLLATERAL)]: (state) => ({
    ...state,
    collateral: {
      isLoading: true,
    },
  }),
  [requestSuccess(DashboardConstants.FETCH_COLLATERAL)]: (state, action) => ({
    ...state,
    collateral: {
      data: action.payload,
      isLoading: false,
    },
  }),
  [requestFail(DashboardConstants.FETCH_COLLATERAL)]: (state, action) => ({
    ...state,
    collateral: {
      error: action.payload,
      isLoading: false,
    },
  }),

  [requestPending(DashboardConstants.FETCH_COLLATERAL_SUMMARY)]: (state) => ({
    ...state,
    collateralSummary: {
      isLoading: true,
    },
  }),
  [requestSuccess(DashboardConstants.FETCH_COLLATERAL_SUMMARY)]: (state, action) => ({
    ...state,
    collateralSummary: {
      data: action.payload,
      isLoading: false,
    },
  }),
  [requestFail(DashboardConstants.FETCH_COLLATERAL_SUMMARY)]: (state, action) => ({
    ...state,
    collateralSummary: {
      error: action.payload,
      isLoading: false,
    },
  }),

  [requestPending(DashboardConstants.FETCH_COLLATERAL_REQUIRED)]: (state) => ({
    ...state,
    collateralRequired: {
      isLoading: true,
    },
  }),
  [requestSuccess(DashboardConstants.FETCH_COLLATERAL_REQUIRED)]: (state, action) => ({
    ...state,
    collateralRequired: {
      data: action.payload,
      isLoading: false,
    },
  }),
  [requestFail(DashboardConstants.FETCH_COLLATERAL_REQUIRED)]: (state, action) => ({
    ...state,
    collateralRequired: {
      error: action.payload,
      isLoading: false,
    },
  }),

  [requestPending(DashboardConstants.FETCH_COLLATERAL_LIQUIDATIONS)]: (state) => ({
    ...state,
    collateralLiquidations: {
      isLoading: true,
    },
  }),
  [requestSuccess(DashboardConstants.FETCH_COLLATERAL_LIQUIDATIONS)]: (state, action) => ({
    ...state,
    collateralLiquidations: {
      data: action.payload,
      isLoading: false,
    },
  }),
  [requestFail(DashboardConstants.FETCH_COLLATERAL_LIQUIDATIONS)]: (state, action) => ({
    ...state,
    collateralLiquidations: {
      error: action.payload,
      isLoading: false,
    },
  }),
  [requestSuccess(AuthConstants.AUTH_SIGN_OUT)]: () => ({
    ...initialState,
  }),
}, initialState);
